import { IReactionDisposer, action, computed, makeObservable, observable, reaction, runInAction } from 'mobx';
import queryString from 'query-string';

import { ENDPOINTS } from '@/configs/endpoints';
import { NewsEntity, NewsFullResponseApi, newsSchema } from '@/entities/News';
import { searchNewsSchema } from '@/entities/News';
import { notificationsStore } from '@/stores/global/RootStore';
import { ILocalStore } from '@/stores/interfaces/ILocalStore';
import { CollectionStore } from '@/stores/local/shared/CollectionStore';
import { MetaStore } from '@/stores/local/shared/MetaStore';
import call from '@/utils/api';
import { schemeParseMultiple } from '@/utils/schemeParse';

import { FieldTextStore } from '../../shared/form/FieldTextStore';

import { NewsDigestSelectStore } from './DigestSelectStore';
import { NewsCategoriesStore } from './NewsCategoriesStore';

export type SidebarNewsStoreParams = {
  organizationId: number;
};

type PrivateFields = '_organizationId' | '_page' | '_total' | '_pageSize' | '_searchNews';

export const PAGE_SIZE = 20;

type FetchNewsOptions = {
  withMeta?: boolean;
  reset?: boolean;
};

export class SidebarNewsStore implements ILocalStore {
  readonly initMeta = new MetaStore();
  readonly meta = new MetaStore();
  readonly nextPageMeta = new MetaStore();
  readonly searchMeta = new MetaStore();
  readonly collection = new CollectionStore<NewsEntity, NewsEntity>({
    parseItem: (data: NewsEntity) => data,
    getEntityKey: (data: NewsEntity) => String(data.id),
  });
  readonly digestSelectStore: NewsDigestSelectStore;
  readonly categoriesStore: NewsCategoriesStore;
  readonly searchField = new FieldTextStore();

  private _organizationId: number;
  private _page = 1;
  private _total = 0;
  private _pageSize = PAGE_SIZE;
  private _selectedDigestId: number | null = null;
  private _initInProgress = false;
  private _isInitialized = false;
  private readonly _digestReactionDisposer: IReactionDisposer;
  private readonly _categoriesReactionDisposer: IReactionDisposer;

  activeNewsId: number | null = null;

  constructor({ organizationId }: SidebarNewsStoreParams) {
    this._organizationId = organizationId;

    makeObservable<SidebarNewsStore, PrivateFields>(this, {
      activeNewsId: observable,
      _page: observable,
      _total: observable,
      _pageSize: observable,
      _organizationId: observable,
      organizationId: computed,
      fetchNews: action.bound,
      fetchMoreNews: action.bound,
      _searchNews: action.bound,
      setActiveId: action.bound,
    });

    this.digestSelectStore = new NewsDigestSelectStore({ organizationId });

    this.categoriesStore = new NewsCategoriesStore({ organizationId });

    this._digestReactionDisposer = reaction(
      () => this.digestSelectStore.serverFormat,
      (digestKey) => {
        this._resetSearchQuery();

        let nextDigestId: number | null = null;

        const numeric = digestKey !== null ? Number(digestKey) : Number.NaN;

        nextDigestId = Number.isNaN(numeric) ? null : numeric;

        runInAction(() => {
          this._selectedDigestId = nextDigestId;
          this._page = 1;
          this._total = 0;
          this._pageSize = PAGE_SIZE;
          this.collection.clear();
          this.meta.reset();
          this.nextPageMeta.reset();
        });

        if (this._initInProgress) {
          return;
        }

        this.fetchNews({ reset: true });
      },
    );

    this._categoriesReactionDisposer = reaction(
      () => this.categoriesStore.value,
      () => {
        this._resetSearchQuery();

        runInAction(() => {
          this._page = 1;
          this._total = 0;
          this._pageSize = PAGE_SIZE;
          this.collection.clear();
          this.meta.reset();
          this.nextPageMeta.reset();
        });

        if (this._initInProgress) {
          return;
        }

        this.fetchNews({ reset: true });
      },
    );
  }

  get organizationId() {
    return this._organizationId;
  }

  isActiveId = (id: number) => {
    return this.activeNewsId === id;
  };

  setActiveId = (id: number) => {
    this.activeNewsId = id;
  };

  get hasMore() {
    return this.collection.items.length < this._total;
  }

  async fetchNews(options: FetchNewsOptions = {}) {
    const { withMeta = true, reset = false } = options;

    const useDigest = this._selectedDigestId !== null;
    const useSearch = this.searchField.value && this.searchField.value.length > 0;

    if (useSearch) {
      await this._searchNews(options);

      return;
    }

    if (withMeta) {
      this.meta.setLoadedStartMeta();
    }

    const shouldReplace = reset || this._page <= 1;

    const response = await call<NewsFullResponseApi>({
      endpoint: useDigest
        ? ENDPOINTS.news.digestNews(String(this._organizationId), String(this._selectedDigestId))
        : ENDPOINTS.news.list(String(this._organizationId)),
      data: {
        search: this.searchField.serverFormat,
        categories_ids: this.categoriesStore.isSelectedAllCategories ? [] : this.categoriesStore.serverFormat,
        page: this._page,
        page_size: this._pageSize,
      },
    });

    if (response.isError) {
      if (withMeta) {
        this.meta.setLoadedErrorMeta();
      }

      return;
    }

    const parsed = schemeParseMultiple(newsSchema, response.data.items);

    runInAction(() => {
      if (shouldReplace) {
        this.collection.fromArray(parsed);
      } else {
        this.collection.pushRaws(parsed);
      }

      this._total = response.paginator?.total_count || 0;
      this._pageSize = response.paginator?.page_size || PAGE_SIZE;
      this._page = response.paginator?.page || 1;

      if (withMeta) {
        this.meta.setLoadedSuccessMeta();
      }
    });
  }

  async fetchMoreNews() {
    if (!this.hasMore) {
      return;
    }

    if (this.nextPageMeta.isLoading) {
      return;
    }

    this.nextPageMeta.setLoadedStartMeta();
    this._page++;
    await this.fetchNews({ withMeta: false });

    this.nextPageMeta.setLoadedSuccessMeta();
  }

  init = async () => {
    if (this._initInProgress || this._isInitialized) {
      return;
    }

    this._initInProgress = true;
    this.initMeta.setLoadedStartMeta();

    try {
      await Promise.all([this.digestSelectStore.load(true), this.categoriesStore.load()]);

      if (this.digestSelectStore.options.length > 0) {
        this.digestSelectStore.setValue([this.digestSelectStore.options[0]], { noTouch: true });
      }

      await this.fetchNews({ reset: true });

      this.initMeta.setLoadedSuccessMeta();
      this._isInitialized = true;
    } catch {
      this.initMeta.setLoadedErrorMeta();
    } finally {
      this._initInProgress = false;
    }
  };

  private async _searchNews(options: FetchNewsOptions = {}) {
    const { withMeta = true, reset = false } = options;

    if (withMeta) {
      this.searchMeta.setLoadedStartMeta();
    }

    const shouldReplace = reset || this._page <= 1;

    const response = await call<NewsFullResponseApi>({
      endpoint: ENDPOINTS.news.searchNews(String(this._organizationId)),
      method: 'POST',
      config: {
        params: {
          search: this.searchField.serverFormat,
          categories_ids: this.categoriesStore.isSelectedAllCategories ? [] : this.categoriesStore.serverFormat,
          page: this._page,
          page_size: this._pageSize,
          period: this.digestSelectStore.serverFormat,
          result_count: 10,
        },
        paramsSerializer: {
          serialize: (params) => queryString.stringify(params, { skipNull: true }),
        },
      },
    });

    if (response.isError) {
      if (withMeta) {
        this.searchMeta.setLoadedErrorMeta();
      }

      notificationsStore.showError(response.error.response?.data.message ?? 'Не удалось найти новости');

      return;
    }

    const parsed = schemeParseMultiple(searchNewsSchema, response.data.items);

    runInAction(() => {
      if (shouldReplace) {
        this.collection.fromArray(parsed);
      } else {
        this.collection.pushRaws(parsed);
      }

      this._total = response.paginator?.total_count || 0;
      this._pageSize = response.paginator?.page_size || PAGE_SIZE;
      this._page = response.paginator?.page || 1;

      if (withMeta) {
        this.searchMeta.setLoadedSuccessMeta();
      }
    });
  }

  private _resetSearchQuery() {
    if (!this.searchField.value) {
      return;
    }

    this.searchField.reset();
    this.searchMeta.reset();
  }

  destroy() {
    this.initMeta.destroy();
    this.meta.destroy();
    this.nextPageMeta.destroy();
    this.searchMeta.destroy();
    this.collection.destroy();
    this.digestSelectStore.destroy();
    this.categoriesStore.destroy();
    this._digestReactionDisposer();
    this._categoriesReactionDisposer();
  }
}
